import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition, ConnectionPositionPair, ɵɵCdkFixedSizeVirtualScroll, ɵɵCdkVirtualForOf, ɵɵCdkVirtualScrollViewport } from '@angular/cdk/overlay';
import { TitleCasePipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatChip, MatChipAvatar } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatDivider, MatListOption, MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { MatFormField, MatLabel, MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { EnpsField } from '@src/suite/common/data-models/fields/enps-field';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { selectListIsLargeView } from '@src/suite/common/store/breakpoints/breakpoints.selectors';
import { selectFieldDefsDict } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { RecordHttpActions, TopicRecordActions } from '@src/suite/common/store/records/records.actions';
import { selectAttachedTopicsRecords, selectCurrentSelectedViewState, selectSavedViewStates } from '@src/suite/common/store/records/records.selectors';
import { WireApiActions } from '@src/suite/discover/store/actions/discover.actions';
import { selectIsAttachToTopicListLoading } from '@src/suite/discover/store/selectors/discover.selectors';

import { LoaderComponent } from '../../loader/loader';
import { AvatarSmallComponent } from '../components/avatar/avatar-small';
import { TopicItemCondensedComponent } from '../components/topic-item-condensed/topic-item-condensed';
import { StTextAreaOverlayComponent } from '../controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { TextFieldComponent } from '../controls/text-field/text-field';
import { TopicTypeComponent } from '../controls/topic-type/topic-type';

@Component({
    selector: 'st-item-attach-to-dialog',
    imports: [
        AvatarSmallComponent,
        CdkConnectedOverlay,
        CdkOverlayOrigin,
        LoaderComponent,
        MatButton,
        MatButtonToggle,
        MatButtonToggleGroup,
        MatChip,
        MatChipAvatar,
        MatDivider,
        MatFormField,
        MatIcon,
        MatLabel,
        MatListOption,
        MatOption,
        MatSelect,
        MatSelectionList,
        MatTooltip,
        ɵɵCdkFixedSizeVirtualScroll,
        ɵɵCdkVirtualForOf,
        ɵɵCdkVirtualScrollViewport,
        StTextAreaOverlayComponent,
        TextFieldComponent,
        TitleCasePipe,
        TopicItemCondensedComponent,
        TopicTypeComponent,
    ],
    templateUrl: './st-item-attach-to-dialog.html',
    styleUrl: './st-item-attach-to-dialog.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-item-attach-to-dialog',
    },
})
export class StItemAttachToDialogComponent implements OnInit {
    store = inject(Store);
    dialogRef = inject(MatDialogRef<StItemAttachToDialogComponent>);
    globalTablesService = inject(GlobalTablesService);
    inputData: StorytellingStoryRecord | StorytellingWireRecord | StorytellingAssignmentRecord = inject(MAT_DIALOG_DATA);

    // Queries
    readonly overlay = viewChild(CdkConnectedOverlay);

    savedViewDropdownVisible = true;
    searchTerm = '';
    openPanelId: string = null;
    PSTFieldNames = PSTFieldNames;

    //signals
    readonly selectedTopicRecord = signal<StorytellingTopicRecord>(null);

    selectIsAttachToTopicListLoading = this.store.selectSignal(selectIsAttachToTopicListLoading);
    languageSettings = this.store.selectSignal(selectI18N)();
    selectSavedViewStates = this.store.selectSignal(selectSavedViewStates);
    currentViewState = this.store.selectSignal(selectCurrentSelectedViewState);

    //computeds
    readonly selectTopics = computed(() => this.store.selectSignal(selectAttachedTopicsRecords)());
    readonly desk = computed(() => (this.store.selectSignal(selectFieldDefsDict)()?.[PSTFieldNames.PSTDesk] as FieldDef)?.DefaultCaption);
    readonly status = computed(() => (this.store.selectSignal(selectFieldDefsDict)()?.[PSTFieldNames.PSTTopicStatus] as FieldDef)?.DefaultCaption);
    readonly description = computed(() => (this.store.selectSignal(selectFieldDefsDict)()?.[PSTFieldNames.Description] as FieldDef)?.DefaultCaption);
    readonly owners = computed(() => (this.store.selectSignal(selectFieldDefsDict)()?.[PSTFieldNames.PSTTopicStoryOwner] as FieldDef)?.DefaultCaption);
    readonly vs = computed(() => this.store.selectSignal(selectCurrentViewState)());

    readonly loading = computed(() => {
        return this.selectIsAttachToTopicListLoading();
    });

    readonly shouldShowCustomFields = computed(() => {
        return this.viewStateCustomCondensedItems()?.length > 0 && this.store.selectSignal(selectListIsLargeView)();
    });

    readonly viewStateCustomCondensedItems = computed(() => {
        if (
            this.vs().PSTViewStateType === ViewStateTypes.TOPIC ||
            this.vs().PSTViewStateType === ViewStateTypes.SINGLEFEED ||
            this.vs().PSTViewStateType === ViewStateTypes.SEARCH ||
            this.vs().PSTViewStateType === ViewStateTypes.WIRESEARCH
        )
            return (this.vs() as StorytellingTopicViewStateRecord).PSTViewStateTopicCardCustomItems;

        return [];
    });

    readonly connectedPositions: ConnectedPosition[] = [
        new ConnectionPositionPair({ originX: 'end', originY: 'top' }, { overlayX: 'start', overlayY: 'top' }),
        new ConnectionPositionPair({ originX: 'end', originY: 'bottom' }, { overlayX: 'start', overlayY: 'bottom' }),
    ];

    ngOnInit() {
        this.store.dispatch(TopicRecordActions.loadTopicViewStates());
    }

    toggleTabVisibility(savedViewDropdownVisible: boolean) {
        this.savedViewDropdownVisible = savedViewDropdownVisible;
        if (this.savedViewDropdownVisible) {
            // if we are switching from search to saved views, and there is a saved view selected
            // we need to prefetch again
            this.searchTerm = '';
            this.store.dispatch(TopicRecordActions.reloadTopicsBasedOnCurrentViewState({ viewState: this.selectSavedViewStates()?.find(view => view?.Guid === this.currentViewState().Guid) }));
        } else {
            // clear available records whenever we switch to search view
            // this.castService.availableRecords = new Array<Record>();
        }
    }

    onSearchHandler(value: string | number) {
        this.store.dispatch(TopicRecordActions.topicSearchTerm({ searchTerm: value.toString() }));
    }

    onAttachItem() {
        if (!this.selectedTopicRecord()) {
            return;
        }
        if (this.inputData?.Type === ObjectTypes.SCRIPT) {
            const record = this.inputData as StorytellingStoryRecord;
            const fieldsToUpdate: EnpsField[] = [];

            if (!record?.PSTStoryTopicGUID) {
                fieldsToUpdate.push({ key: PSTFieldNames.PSTStoryTopicGUID, value: this.selectedTopicRecord()?.Guid });
                this.store.dispatch(RecordHttpActions.saveRecordChanges({ fieldsToUpdate, record: record }));
                this.dialogRef.close();
            }
            return;
        } else if (this.inputData?.Type === ObjectTypes.ASSIGNMENT) {
            const record = this.inputData as StorytellingAssignmentRecord;
            this.store.dispatch(RecordHttpActions.copyAssignmentToTopic({ record: record, targetTopicGuid: this.selectedTopicRecord()?.Guid }));
            this.dialogRef.close();
            return;
        }
        return this.store.dispatch(WireApiActions.attachItem({ wire: this.inputData, topic: this.selectedTopicRecord() }));
    }

    cancelNew() {
        this.dialogRef.close();
    }

    trackByGuid(_index, node) {
        if (node) {
            return node?.Guid;
        }
    }

    onViewStateGuidChanged(viewState: MatSelectChange) {
        this.store.dispatch(TopicRecordActions.setCurrentTopicViewState({ viewState: this.selectSavedViewStates()?.find(view => view?.Guid === viewState?.value) }));
    }

    onSelectionChange(event: MatSelectionListChange) {
        const selectedOptions = event?.source?.selectedOptions?.selected;
        if (selectedOptions?.length > 0) {
            this.selectedTopicRecord.set(selectedOptions[0]?.value);
        } else {
            this.selectedTopicRecord.set(null);
        }
    }

    panelAttached() {
        const overlayElement = this.overlay()?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener('mouseleave', () => {
                this.openPanelId = null;
            });
        }
    }

    onMouseMove(event: MouseEvent) {
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-item-attach-to-container-overlay')) {
            this.openPanelId = null;
        }
    }

    onMouseEnter(recordGuid: string) {
        this.openPanelId = recordGuid;
    }

    isPanelOpenForRecord(recordGuid: string): boolean {
        return this.openPanelId === recordGuid;
    }
}
